import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from "react"

// --- State ---

export interface ConnectorFormState {
  connectorType: string
  connectorName: string
  apiToken: string
  credentialValues: Record<string, string>
  useMock: boolean
  fixture: string
  error: string | null
}

const initialState: ConnectorFormState = {
  connectorType: "",
  connectorName: "",
  apiToken: "",
  credentialValues: {},
  useMock: false,
  fixture: "bsu",
  error: null,
}

// --- Actions ---

interface ConnectorFormActions {
  setConnectorType: (type: string) => void
  setConnectorName: (name: string) => void
  setApiToken: (token: string) => void
  setCredentialValue: (key: string, value: string) => void
  setUseMock: (useMock: boolean) => void
  setFixture: (fixture: string) => void
  setError: (error: string | null) => void
  reset: () => void
}

// --- Contexts (split to prevent unnecessary re-renders) ---

const ConnectorFormStateContext = createContext<ConnectorFormState | undefined>(undefined)
const ConnectorFormActionsContext = createContext<ConnectorFormActions | undefined>(undefined)

// --- Provider ---

export const ConnectorFormProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<ConnectorFormState>(initialState)

  const setConnectorType = useCallback((connectorType: string) => {
    setState((prev) => ({
      ...prev,
      connectorType,
      error: null,
    }))
  }, [])

  const setConnectorName = useCallback((connectorName: string) => {
    setState((prev) => ({
      ...prev,
      connectorName,
      error: null,
    }))
  }, [])

  const setApiToken = useCallback((apiToken: string) => {
    setState((prev) => ({
      ...prev,
      apiToken,
      error: null,
    }))
  }, [])

  const setCredentialValue = useCallback((key: string, value: string) => {
    setState((prev) => ({
      ...prev,
      credentialValues: { ...prev.credentialValues, [key]: value },
      error: null,
    }))
  }, [])

  const setUseMock = useCallback((useMock: boolean) => {
    setState((prev) => ({
      ...prev,
      useMock,
      error: null,
    }))
  }, [])

  const setFixture = useCallback((fixture: string) => {
    setState((prev) => ({
      ...prev,
      fixture,
      error: null,
    }))
  }, [])

  const setError = useCallback((error: string | null) => {
    setState((prev) => ({
      ...prev,
      error,
    }))
  }, [])

  const reset = useCallback(() => {
    setState(initialState)
  }, [])

  const actions = useMemo<ConnectorFormActions>(
    () => ({
      setConnectorType,
      setConnectorName,
      setApiToken,
      setCredentialValue,
      setUseMock,
      setFixture,
      setError,
      reset,
    }),
    [setConnectorType, setConnectorName, setApiToken, setCredentialValue, setUseMock, setFixture, setError, reset],
  )

  return (
    <ConnectorFormStateContext.Provider value={state}>
      <ConnectorFormActionsContext.Provider value={actions}>
        {children}
      </ConnectorFormActionsContext.Provider>
    </ConnectorFormStateContext.Provider>
  )
}

// --- Hooks ---

export function useConnectorFormState(): ConnectorFormState {
  const context = useContext(ConnectorFormStateContext)
  if (!context) throw new Error("useConnectorFormState must be used within ConnectorFormProvider")
  return context
}

export function useConnectorFormActions(): ConnectorFormActions {
  const context = useContext(ConnectorFormActionsContext)
  if (!context) throw new Error("useConnectorFormActions must be used within ConnectorFormProvider")
  return context
}
